<script>
  /**
   * Shared field visibility menu for block and embed card tools
   * Renders toggles for field visibility with consistent UI
   */
  import Switch from '#lib/Switch.svelte';
  import t from '#lib/i18n.svelte.js';
  import ButtonLabel from '#lib/ButtonLabel.svelte';
  import SegmentedControl from '#lib/SegmentedControl.svelte';
  import { TITLE_LEVEL_PARAGRAPH } from '#lib/StoryBlockModel.svelte.js';

  let {
    /** @type {import('./StoryBlockModel.svelte.js').default} The block object with visibility properties */
    block,
    /** @type {'block' | 'embed'} Context for different field sets */
    context = 'block',
  } = $props();

  /**
   * One list drives the whole menu, so every row is the same object: icon, label, control.
   * `control` only decides what sits in the right-hand column — never the row's metrics.
   */
  const block_rows = [
    { control: 'switch', field: 'show_preamble', label: t('preamble'), icon: 'preamble' },
    // Sits where the heading sits in the block: after the preamble, before the body.
    { control: 'heading', label: t('set_heading_size'), icon: 'title-1' },
    { control: 'switch', field: 'show_description', label: t('description'), icon: 'body' },
    { control: 'switch', field: 'show_links', label: t('links'), icon: 'link' },
  ];

  // Embed/entry preview field toggles
  const embed_rows = [
    { control: 'switch', field: 'show_entry_preview_preamble', label: t('preamble'), icon: 'preamble' },
    { control: 'switch', field: 'show_entry_preview_title', label: t('title'), icon: 'title-1' },
    { control: 'switch', field: 'show_entry_preview_description', label: t('description'), icon: 'body' },
    { control: 'switch', field: 'show_entry_preview_images', label: t('images'), icon: 'image' },
    { control: 'switch', field: 'show_entry_preview_button', label: t('button'), icon: 'link' },
  ];

  /**
   * Every row hands ButtonLabel the same variant. That is what keeps row height, label
   * size, icon size and both inline paddings identical across control types — and in line
   * with the plain menu items this menu is stacked below.
   * NOTE: `flush` used to be set here for the heading row only. It zeroes
   * `--button-label-padding-inline`, which pulled that row's icon ~19px left of all others.
   */
  const row_variant = 'ghostly';

  // The Program layout can fill itself from the calendar
  const program_row = { control: 'switch', field: 'program_automatic', label: t('program_automatic'), icon: 'calendar' };
  let rows = $derived(
    context === 'block'
      ? (block.layout === 148 ? [...block_rows, program_row] : block_rows)
      : embed_rows
  );
  let title_level = $derived(Number(block.title_level ?? 0));

  // An em dash reads as "none" next to H1/H2/H3, and P is the title in paragraph style;
  // the tooltips spell both out.
  const title_level_options = [
    { value: '0', title: '—', description: t('no_heading') },
    { value: '1', title: 'H1', description: t('heading_level', { level: 1 }) },
    { value: '2', title: 'H2', description: t('heading_level', { level: 2 }) },
    { value: '3', title: 'H3', description: t('heading_level', { level: 3 }) },
    { value: String(TITLE_LEVEL_PARAGRAPH), title: 'P', description: t('heading_paragraph_style') },
  ];
</script>

<div class="field-visibility-menu flex-column">
  {#each rows as { control, field, label, icon }}
    {#if control === 'heading'}
      <!-- A <span> host, because ButtonLabel gives every :is(a, button, span) wrapper the
           same outer box. This row inherits the Switch rows' box instead of copying it. -->
      <span class='heading-row' class:heading-on={title_level > 0}>
        <ButtonLabel
          variant={row_variant}
          align='between'
          start_icon={icon}
          hover_effect={false}
        >
          {label}
          {#snippet end_icon()}
            <SegmentedControl
              class='heading-levels'
              options={title_level_options}
              value={String(title_level)}
              on_select={(level) => { block.title_level = Number(level) }}
            />
          {/snippet}
        </ButtonLabel>
      </span>
    {:else}
      <Switch
        value={block[field]}
        toggle={(val) => { block[field] = val }}
        start_icon={icon}
        variant={row_variant}
      >
        {label}
      </Switch>
    {/if}
  {/each}
</div>

<style lang="postcss">
  /* The heading row is the only row whose control has more than two states. It is drawn as
     a multi-position sibling of the Switch — same track height, same pill, same background
     steps — so it joins the rhythm of the switches instead of competing with them. */
  .field-visibility-menu :global(.segmented-control.button-group.heading-levels) {
    /* Track geometry taken from Switch.svelte: bullet + 2 * track padding */
    --input-min-height: calc(var(--size-5) + 2 * var(--size-1));
    --input-block-spacing: var(--size-0);
    --group-border-radius: var(--round);
    /* One size step down — the step `text-compact` uses — so the segments stay subordinate
       to the row labels they sit next to. */
    --size-modifier: 0.875;
    /* Equal columns: segments sized to their own labels would read as a ragged
       control next to the even Switch tracks. */
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: 1fr;
    background-color: var(--color-default-background-200);
    /* No ring and no margin: the filled track is the shape, and this control's right edge
       has to land on the same axis as the Switch tracks. */
    box-shadow: none;
    margin: 0;
  }
  /* The button-group default lightens two steps on hover. Match the Switch's inactive
     track instead, so both controls respond identically. */
  .field-visibility-menu :global(.segmented-control.button-group.heading-levels:hover) {
    background-color: var(--color-default-background-100);
  }
  /* Unselected segments are label only — the group's track is the surface. */
  .field-visibility-menu :global(.heading-levels .button-label.segment) {
    --button-background-color: transparent;
  }
  /* The selected level is the thumb, drawn like the Switch bullet: the plain background
     colour, no elevation. */
  .field-visibility-menu :global(.heading-levels .button-label.segment-selected) {
    --button-background-color: var(--color-default-background);
    --button-text-color: var(--color-foreground, var(--color-default-foreground));
    box-shadow: none;
  }
  /* ...and it takes the editing colour as soon as a level is set, which is the same "this
     field is on" signal the switches in this menu use. The em dash keeps the neutral thumb,
     so "no heading" looks exactly like an off switch. */
  .heading-row.heading-on :global(.heading-levels .button-label.segment-selected) {
    --button-background-color: var(--editing);
    --button-text-color: var(--editing-text);
  }
</style>
